/**
 * Customer 360 View type definitions.
 *
 * Maps to backend Pydantic schemas in app/schemas/customer.py.
 */

// =============================================================================
// Customer List
// =============================================================================

export interface CustomerListItem {
  id: string;
  sage_customer_no: string;
  slug: string | null;
  customer_name: string | null;
  city: string | null;
  state: string | null;
  salesperson_no: string | null;
  salesperson_name: string | null;
  regional_responsible_id: string | null;
  regional_responsible_name: string | null;
  customer_status: string | null;
  current_balance: string | null;
  ytd_sales: string | null;
  lytd_sales: string | null;
  ly_total_sales: string | null;
  all_time_sales: string | null;
  ytd_vs_lytd_pct: number | null;
  last_order_date: string | null;
  industry_type: string | null;
  customer_type: string | null;
}

export interface CustomerListResponse {
  customers: CustomerListItem[];
  total: number;
  limit: number;
  offset: number;
}

/** Core salesperson ("regional responsible") option for filter dropdowns. */
export interface RegionalResponsibleOption {
  id: string;
  name: string;
}

// =============================================================================
// Customer 360 Profile
// =============================================================================

export interface CustomerContactInfo {
  id: string;
  sage_customer_no: string;
  slug: string | null;
  customer_name: string | null;
  address_line1: string | null;
  address_line2: string | null;
  address_line3: string | null;
  city: string | null;
  state: string | null;
  zip_code: string | null;
  telephone_no: string | null;
  telephone_ext: string | null;
  fax_no: string | null;
  email_address: string | null;
  contact_code: string | null;
  customer_status: string | null;
  customer_type: string | null;
  price_level: string | null;
  terms_code: string | null;
  credit_hold: string | null;
  credit_limit: string | null;
  cp_email_list_shipping: string | null;
  date_created: string | null;
}

export interface CustomerSalesperson {
  salesperson_no: string | null;
  salesperson_name: string | null;
  email_address: string | null;
  telephone_no: string | null;
  /** In-house core salesperson this rep code reports to (regional_rep_mappings); null when unmapped. */
  regional_responsible_id?: string | null;
  regional_responsible_name?: string | null;
}

export interface CustomerEnrichment {
  industry_type: string | null;
  industry_type_source: string | null;
  customer_type: string | null;
  customer_type_reason: string | null;
  primary_contact_name: string | null;
  primary_contact_phone: string | null;
  primary_contact_email: string | null;
  company_group: string | null;
  is_sphere_member: boolean;
  udf_business: string | null;
}

export interface CustomerSalesMetrics {
  ytd_sales: string;
  lytd_sales: string;
  ly_total_sales: string;
  ytd_vs_lytd_pct: number | null;
  last_order_date: string | null;
}

export interface CustomerCategoryBreakdown {
  category: string;
  amount: string;
  percentage: number;
}

export interface CustomerARaging {
  current_balance: string | null;
  aging_category1: string | null;
  aging_category2: string | null;
  aging_category3: string | null;
  aging_category4: string | null;
  open_order_amt: string | null;
  avg_days_payment_invoice: string | null;
  avg_days_over_due: string | null;
  last_payment_amt: string | null;
  highest_stmnt_balance: string | null;
}

export interface CustomerSalesHistoryItem {
  id: string;
  invoice_no: string | null;
  invoice_date: string | null;
  item_code: string | null;
  item_description: string | null;
  quantity_shipped: string | null;
  extension_amt: string | null;
  salesperson_name_bill_to: string | null;
}

export interface CustomerOrderItem {
  id: string;
  status: string;
  source_type: string;
  customer_no: string;
  salesperson_name: string;
  subtotal: string;
  total: string | null;
  payment_terms: string | null;
  sage_order_no: string | null;
  po_number: string | null;
  line_item_count: number;
  created_at: string;
  updated_at: string;
}

export type BOLStatus = 'draft' | 'final';

export interface CustomerBOLItem {
  id: string;
  order_id: string | null;
  sage_order_no: string | null;
  filename: string;
  status: BOLStatus;
  mime_type: string;
  file_size_bytes: number | null;
  drive_web_view_link: string | null;
  drive_modified_time: string;
  last_synced_at: string;
  /** Content-derived name ("BOL AU01354 - Dealer Depot - 2026-08-24.pdf"); null on old rows. */
  display_name?: string | null;
  page_number?: number | null;
  /** True when we hold our own GCS copy (viewing does not depend on Drive). */
  stored?: boolean;
}

export interface CustomerSageImportOrderItem {
  id: string;
  sage_order_no: string | null;
  processing_status: string;
  udf_required_by: string | null;
  customer_po_no: string | null;
  warehouse_code: string;
  is_test: boolean;
  line_item_count: number;
  linked_order_id: string | null;
  created_at: string;
}

export interface CustomerQuoteItem {
  id: string;
  source_id: number;
  status: string | null;
  created_date: string | null;
  expiration_date: string | null;
  project_name: string | null;
  freight_type: string | null;
  product_count: number;
  estimated_total: number | null;
}

export interface CustomerSpecialPricingItem {
  id: string;
  source_id: number;
  effective_date: string | null;
  expiration_date: string | null;
  base_discount: number | null;
  payment_terms: string | null;
  note: string | null;
  is_active: boolean | null;
  /** False when flagged active but past its expiration (or before its start): no longer prices orders. */
  in_force: boolean;
  product_count: number;
  source_customer_no: string | null;
  via_linked_customer: boolean;
}

export interface CustomerSpecialPricingProductItem {
  item_code: string | null;
  /** Catalog display name (udf_fulldesc > item_code_desc); null when unknown. */
  item_description: string | null;
  special_price: number | null;
  pricing_type: string | null;
  tier_price_1: number | null;
  tier_price_2: number | null;
  tier_price_3: number | null;
  tier_price_2_min: number | null;
  tier_price_3_min: number | null;
  note: string | null;
  is_active: boolean | null;
  source_customer_no: string | null;
  via_linked_customer: boolean;
}

export interface CustomerShipLocationItem {
  id: string;
  customer_location_no: string | null;
  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  state: string | null;
  zip_code: string | null;
  telephone_no: string | null;
  email_address: string | null;
  is_primary: boolean;
}

export interface CustomerShipToAddressItem {
  id: string;
  ship_to_code: string | null;
  ship_to_name: string | null;
  ship_to_address1: string | null;
  ship_to_address2: string | null;
  ship_to_address3: string | null;
  ship_to_city: string | null;
  ship_to_state: string | null;
  ship_to_zip_code: string | null;
  ship_to_country_code: string | null;
  telephone_no: string | null;
  telephone_ext: string | null;
  fax_no: string | null;
  email_address: string | null;
  contact_code: string | null;
  salesperson_no: string | null;
  warehouse_code: string | null;
  tax_schedule: string | null;
  ship_via: string | null;
  residential_address: string | null;
  udf_inactive: string | null;
}

export interface CustomerSalesContactItem {
  id: string;
  name: string;
  phone: string | null;
  email: string | null;
  location_id: number | null;
  source_date_added: string | null;
}

export interface CustomerEmailItem {
  id: string;
  subject: string | null;
  from_address: string | null;
  to_address: string | null;
  received_at: string | null;
  routing_status: string | null;
  match_method: string | null;
  classification_category: string | null;
  classification_confidence: number | null;
  llm_summary: string | null;
}

export interface CustomerNoteItem {
  id: string;
  contact_name: string;
  role: string;
  note: string;
  source: string | null;
  created_at: string;
}

export interface CustomerAccountUpdateItem {
  id: string;
  text: string;
  source: string | null;
  normalized_data: Record<string, unknown> | null;
  created_at: string;
}

export interface CustomerTripReportEventItem {
  id: string;
  chunk_type: string;
  event_type: string | null;
  event_date: string | null;
  summary: string | null;
  company_name: string | null;
  salesperson_id: string | null;
  salesperson_name: string | null;
  sentiment: string | null;
  city: string | null;
  state: string | null;
  contacts_mentioned: string[] | null;
  products_mentioned: string[] | null;
  action_items: string[] | null;
}

export interface CustomerTimelineItem {
  event_type: string;
  super_type: string | null;
  title: string;
  description: string | null;
  timestamp: string;
  metadata: Record<string, unknown> | null;
}

export interface Customer360Profile {
  contact_info: CustomerContactInfo;
  salesperson: CustomerSalesperson | null;
  enrichment: CustomerEnrichment | null;
  sales_metrics: CustomerSalesMetrics;
  category_breakdown: CustomerCategoryBreakdown[];
  ar_aging: CustomerARaging;
  recent_invoices: CustomerSalesHistoryItem[];
  recent_invoices_total: number;
  orders: CustomerOrderItem[];
  orders_total: number;
  bols: CustomerBOLItem[];
  bols_total: number;
  sage_import_orders: CustomerSageImportOrderItem[];
  sage_import_orders_total: number;
  quotes: CustomerQuoteItem[];
  quotes_total: number;
  special_pricing: CustomerSpecialPricingItem[];
  special_pricing_products: CustomerSpecialPricingProductItem[];
  special_pricing_products_total: number;
  ship_locations: CustomerShipLocationItem[];
  sage_ship_to_addresses: CustomerShipToAddressItem[];
  sales_contacts: CustomerSalesContactItem[];
  emails: CustomerEmailItem[];
  emails_total: number;
  trip_report_events: CustomerTripReportEventItem[];
  trip_report_events_total: number;
  notes: CustomerNoteItem[];
  account_updates: CustomerAccountUpdateItem[];
  /** Free-text order/handling notes synced from Sage AR_Customer.UDF_ORDER_NOTES. */
  sage_order_notes: string | null;
  timeline: CustomerTimelineItem[];
}

// =============================================================================
// Helpers
// =============================================================================

export const CUSTOMER_STATUS_LABELS: Record<string, string> = {
  A: 'Active',
  I: 'Inactive',
  T: 'Temporary',
};

export const CUSTOMER_STATUS_VARIANTS: Record<string, 'success' | 'warning' | 'error' | 'neutral'> = {
  A: 'success',
  I: 'error',
  T: 'warning',
};

export const TIMELINE_EVENT_COLORS: Record<string, string> = {
  invoice: '#2196F3',
  order: '#4CAF50',
  quote: '#FF9800',
  email: '#9C27B0',
  note: '#607D8B',
  update: '#795548',
  call: '#00BCD4',
  visit: '#E91E63',
  meeting: '#3F51B5',
  follow_up: '#FF5722',
  issue: '#F44336',
  activity: '#9E9E9E',
  trip_email: '#AB47BC',
  trip_order: '#66BB6A',
  trip_quote: '#FFA726',
};

export const TIMELINE_EVENT_LABELS: Record<string, string> = {
  invoice: 'Invoice',
  order: 'Order',
  quote: 'Quote',
  email: 'Email',
  note: 'Note',
  update: 'Update',
  call: 'Call',
  visit: 'Visit',
  meeting: 'Meeting',
  follow_up: 'Follow-Up',
  issue: 'Issue',
  activity: 'Activity',
  trip_email: 'Email Notes',
  trip_order: 'Order Notes',
  trip_quote: 'Quote Notes',
  bol_scanned_out: 'Scanned Out',
};

// Super-type grouping: maps each event_type to its parent category.
// Must stay in sync with _EVENT_TYPE_TO_SUPER_TYPE in services/customer/timeline.py.
export const TIMELINE_EVENT_TO_SUPER_TYPE: Record<string, string> = {
  email: 'email',
  trip_email: 'email',
  order: 'order',
  trip_order: 'order',
  bol_scanned_out: 'order',
  quote: 'quote',
  trip_quote: 'quote',
  invoice: 'invoice',
  call: 'call',
  visit: 'visit',
  meeting: 'meeting',
  follow_up: 'other',
  issue: 'other',
  activity: 'other',
  note: 'other',
  update: 'other',
};

// Human-readable labels for each super-type category.
export const TIMELINE_SUPER_TYPE_LABELS: Record<string, string> = {
  email: 'Email',
  order: 'Order',
  quote: 'Quote',
  invoice: 'Invoice',
  call: 'Call',
  visit: 'Visit',
  meeting: 'Meeting',
  other: 'Other',
};

// Colors for super-type filter buttons.
export const TIMELINE_SUPER_TYPE_COLORS: Record<string, string> = {
  email: '#9C27B0',
  order: '#4CAF50',
  quote: '#FF9800',
  invoice: '#2196F3',
  call: '#00BCD4',
  visit: '#E91E63',
  meeting: '#3F51B5',
  other: '#607D8B',
};

// Sub-type labels shown in dropdown menus within grouped filters.
// Only groups with more than one sub-type need these.
export const TIMELINE_SUB_TYPE_LABELS: Record<string, Record<string, string>> = {
  email: { email: 'Inbox Emails', trip_email: 'Email Notes (Trip)' },
  order: { order: 'System Orders', trip_order: 'Order Notes (Trip)' },
  quote: { quote: 'System Quotes', trip_quote: 'Quote Notes (Trip)' },
};
